import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { deepmerge } from '@mui/utils';
import { THEME_PREFIX } from './types.js';
import { produce } from 'immer';
import { isWhite } from '../utils/stringUtils.js';
import brandclub_colors from '../colors/brandclubColors.js';

const processTheme = (themeOptions) => {
    if (!themeOptions)
        return themeOptions;
    const sanitizedThemeOptions = produce(themeOptions, (draft) => {
        var _a, _b, _c, _d, _e, _f, _g;
        var _h, _j;
        draft !== null && draft !== void 0 ? draft : (draft = {});
        /**
         * Recursively updates keys whose value corresponds to any URL.
         * a plain URL string causes the theme cssvars to fail to load which breaks the theme.
         * we wrap the URL in a css url() function to prevent the theme cssvars from failing to load.
         */
        const processThemeUrls = (obj) => {
            Object.keys(obj).forEach((key) => {
                const value = obj[key];
                if (typeof value === 'string' && value.match(/https?:\/\//)) {
                    obj[key] = `url(${value})`;
                    return;
                }
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    processThemeUrls((value !== null && value !== void 0 ? value : {}));
                }
            });
        };
        processThemeUrls(draft);
        // Fix typography lineHeight to prevent malformed font shorthand CSS variables
        if (draft.typography && typeof draft.typography === 'object') {
            const typography = draft.typography;
            Object.keys(typography).forEach((key) => {
                const variant = typography[key];
                if (variant && typeof variant === 'object' && variant.lineHeight === 'inherit') {
                    // Change 'inherit' to 'normal' to prevent malformed font shorthand
                    variant.lineHeight = 'normal';
                }
            });
        }
        let backdropColor = brandclub_colors.black;
        if (((_b = (_a = themeOptions.palette) === null || _a === void 0 ? void 0 : _a.text) === null || _b === void 0 ? void 0 : _b.primary) && !isWhite((_d = (_c = themeOptions.palette) === null || _c === void 0 ? void 0 : _c.text) === null || _d === void 0 ? void 0 : _d.primary)) {
            backdropColor = themeOptions.palette.text.primary;
        }
        (_e = draft.components) !== null && _e !== void 0 ? _e : (draft.components = {});
        (_f = (_h = draft.components).MuiBackdrop) !== null && _f !== void 0 ? _f : (_h.MuiBackdrop = {});
        (_g = (_j = draft.components.MuiBackdrop).styleOverrides) !== null && _g !== void 0 ? _g : (_j.styleOverrides = {});
        // ensure the backdrop is always consistent across all themes
        draft.components.MuiBackdrop.styleOverrides.root = {
            backgroundColor: alpha(backdropColor, 0.21),
            transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            zIndex: 900,
        };
    });
    return sanitizedThemeOptions;
};
const UpdateCustomThemeContext = createContext(null);
const CustomThemeOptionsContext = createContext(undefined);
const useUpdateCustomTheme = () => {
    const _updateCustomTheme = useContext(UpdateCustomThemeContext);
    const customThemeOptions = useContext(CustomThemeOptionsContext);
    if (!_updateCustomTheme || !customThemeOptions) {
        throw new Error('useUpdateCustomTheme must be used within a CustomThemeProvider');
    }
    const updateCustomTheme = useCallback((update, forceUpdate = false) => {
        if (forceUpdate) {
            return _updateCustomTheme(update);
        }
        return _updateCustomTheme((prev) => deepmerge(prev, typeof update === 'function' ? update(prev) : update, { clone: true }));
    }, [_updateCustomTheme, customThemeOptions]);
    return [customThemeOptions, updateCustomTheme];
};
const WithUpdateCustomThemeOptions = (props) => {
    const [themeOptions, setThemeOptions] = useState(props.themeOptions);
    useEffect(() => {
        setThemeOptions(props.themeOptions);
    }, [props.themeOptions]);
    return (React.createElement(UpdateCustomThemeContext.Provider, { value: setThemeOptions },
        React.createElement(CustomThemeOptionsContext.Provider, { value: themeOptions }, props.children)));
};
const CustomThemeProviderConsumer = (props) => {
    const [themeOptions] = useUpdateCustomTheme();
    return (React.createElement(ThemeProvider, { theme: createTheme(Object.assign(Object.assign({}, processTheme(themeOptions)), { cssVariables: {
                cssVarPrefix: THEME_PREFIX,
            } })) }, props.children));
};
const CustomThemeProvider = (props) => {
    return (React.createElement(WithUpdateCustomThemeOptions, { themeOptions: props.themeOptions },
        React.createElement(CustomThemeProviderConsumer, null, props.children)));
};

export { CustomThemeProvider, useUpdateCustomTheme };
//# sourceMappingURL=customTheme.js.map
